import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { asset } from '../engine/assets.ts';
import { BRAND_RED, CORAL, GREEN_BG, GREEN_FG, IG_GRADIENT, INK, LINE, MUTED, PAPER, SHADOW_CARD, YELLOW } from '../ui/theme.ts';

interface Featured {
  badge?: { kind: 'icon' | 'text'; value: string; bg: string; serif?: boolean };
  name?: string;
  sub?: string;
}

/** Swipeable testimonial carousel; the press strip is behind settings.features.featuredStrip. */
export function SocialProof({ step, content, ctx }: StepProps) {
  const list = ctx.shared.testimonials ?? [];
  const [i, setI] = useState(0);
  const drag = useRef<number | null>(null);
  const featured = (ctx.shared.featured ?? []).filter((f) => (f as Featured).name) as Featured[];
  const showFeatured = ctx.config.settings.features.featuredStrip && featured.length > 0;

  useEffect(() => {
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.continue ?? 'Continue', onPress: ctx.next });
  }, [step.id, content.cta]);

  const t = list[i % Math.max(1, list.length)];
  const go = (d: number) => setI((prev) => (prev + d + list.length) % list.length);

  if (!t) return <TitleBlock title={content.title} subtitle={content.subtitle} />;

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" onClick={() => go(-1)} aria-label="Previous" style={circle}>
          <Icon name="caret-left" size={18} />
        </button>
        <div
          key={i}
          onPointerDown={(e) => (drag.current = e.clientX)}
          onPointerUp={(e) => {
            if (drag.current === null) return;
            const dx = e.clientX - drag.current;
            drag.current = null;
            if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          }}
          style={{
            flex: 1,
            padding: 18,
            borderRadius: 24,
            background: PAPER,
            boxShadow: SHADOW_CARD,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            animation: 'fk-in .25s ease-out',
            touchAction: 'pan-y',
          }}
        >
          <span style={{ display: 'inline-flex', gap: 1, color: YELLOW }}>
            {[0, 1, 2, 3, 4].map((s) => (
              <Icon key={s} name="star" size={16} />
            ))}
          </span>
          <div style={{ fontWeight: 500 }}>“{t.quote}”</div>
          <span style={{ alignSelf: 'flex-start', padding: '5px 10px', borderRadius: 999, background: GREEN_BG, color: GREEN_FG, fontSize: 12, fontWeight: 800 }}>{t.result}</span>
          <div style={{ height: 2, background: LINE }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 40, height: 40, borderRadius: '50%', flex: 'none', backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: `url(${asset(t.avatar)})` }} />
            <div>
              <div style={{ fontWeight: 800 }}>{t.name}</div>
              <div style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>{t.handle}</div>
            </div>
          </div>
        </div>
        <button type="button" onClick={() => go(1)} aria-label="Next" style={circle}>
          <Icon name="caret-right" size={18} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
        {list.map((_, d) => (
          <span key={d} style={{ width: 8, height: 8, borderRadius: '50%', background: d === i ? CORAL : LINE }} />
        ))}
      </div>

      {showFeatured ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: MUTED }}>{ctx.shared.ui?.featuredBy ?? 'Featured & trusted by'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${featured.length},1fr)`, gap: 10, width: '100%' }}>
            {featured.map((f) => (
              <div key={f.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 8px', borderRadius: 20, background: PAPER, boxShadow: SHADOW_CARD, textAlign: 'center' }}>
                <span
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: f.badge?.bg === 'igGradient' ? IG_GRADIENT : f.badge?.bg === '#C8102E' ? BRAND_RED : (f.badge?.bg ?? INK),
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontFamily: f.badge?.serif ? 'Georgia, serif' : undefined,
                    fontSize: f.badge?.serif ? 22 : 15,
                    fontWeight: 800,
                  }}
                >
                  {f.badge?.kind === 'icon' ? <Icon name={f.badge.value} size={26} /> : f.badge?.value}
                </span>
                <span style={{ fontSize: 12, fontWeight: 800, lineHeight: 1.2 }}>{f.name}</span>
                <span style={{ fontSize: 12, color: INK, fontWeight: 700, opacity: 0.7 }}>{f.sub}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

const circle: React.CSSProperties = {
  width: 44,
  height: 44,
  flex: 'none',
  borderRadius: '50%',
  border: `2px solid ${LINE}`,
  background: PAPER,
  cursor: 'pointer',
  color: INK,
};
